<template>
  <SectionShell id="hair" num="08" kicker="Hair">
    <template #title>Hair is <span class="hl">one shape</span><br />that happens to have<br />direction inside it.</template>
    <template #lede>
      <p>
        Nobody draws a hundred thousand strands. You draw a single mass, you establish
        where the skull is roundest — because that is where the light lands — and then
        you argue with it: darks in, lights out, darks in again, until it stops looking
        like a helmet.
      </p>
    </template>

    <div class="hairlab">
      <figure class="hairlab__fig card">
        <svg viewBox="0 0 420 480" class="hairlab__svg" role="img"
             :aria-label="`${type} hair built in four steps, showing the band of light`">
          <defs>
            <filter id="hb-blend" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur :stdDeviation="step >= 3 ? 4.5 : 0" />
            </filter>
            <radialGradient id="hb-band" cx="0.5" cy="0.28" r="0.5">
              <stop offset="0%" stop-color="#e8ecf1" stop-opacity="0.55" />
              <stop offset="100%" stop-color="#e8ecf1" stop-opacity="0" />
            </radialGradient>
            <clipPath id="hb-clip"><path :d="mass" /></clipPath>
            <radialGradient id="hb-skin" cx="0.64" cy="0.3" r="0.82">
              <stop offset="0%" stop-color="#c9d0d8" />
              <stop offset="42%" stop-color="#8d959e" />
              <stop offset="76%" stop-color="#4a515a" />
              <stop offset="100%" stop-color="#252b33" />
            </radialGradient>
            <filter id="hb-soft" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="1.5" />
            </filter>
          </defs>

          <rect width="420" height="480" fill="#0a0d12" />

          <!-- the face beneath, deliberately understated -->
          <g opacity="0.95">
            <ellipse cx="210" cy="258" rx="105" ry="140" fill="url(#hb-skin)" />
            <path d="M168 386 L168 428 Q132 442 116 480 L304 480 Q288 442 252 428 L252 386Z"
                  fill="#3f464f" filter="url(#hb-soft)" />
            <ellipse cx="210" cy="398" rx="58" ry="24" fill="#0b0e13" opacity="0.6" filter="url(#hb-soft)" />
            <g filter="url(#hb-soft)">
              <path d="M152 240 Q176 230 198 238" stroke="#2b323b" stroke-width="6" fill="none" stroke-linecap="round" opacity="0.85" />
              <path d="M222 238 Q244 230 268 240" stroke="#2b323b" stroke-width="6" fill="none" stroke-linecap="round" opacity="0.85" />
              <ellipse cx="176" cy="262" rx="13" ry="7" fill="#333b45" />
              <ellipse cx="244" cy="262" rx="13" ry="7" fill="#333b45" />
              <circle cx="176" cy="262" r="4.6" fill="#12161c" />
              <circle cx="244" cy="262" r="4.6" fill="#12161c" />
              <path d="M163 259 Q176 253 189 260" stroke="#12161c" stroke-width="2.6" fill="none" stroke-linecap="round" />
              <path d="M231 260 Q244 253 257 259" stroke="#12161c" stroke-width="2.6" fill="none" stroke-linecap="round" />
              <path d="M200 292 Q194 316 186 328 Q199 337 210 337 Q221 337 234 328 Q226 316 220 292Z" fill="#3c434c" opacity="0.62" />
              <ellipse cx="190" cy="332" rx="5" ry="3.4" fill="#12161c" />
              <ellipse cx="230" cy="332" rx="5" ry="3.4" fill="#12161c" />
              <path d="M186 358 Q210 352 234 358 Q210 368 186 358Z" fill="#252c35" />
              <path d="M188 358 Q210 354 232 358" stroke="#12161c" stroke-width="2" fill="none" stroke-linecap="round" />
            </g>
          </g>

          <!-- 1. the mass -->
          <path :d="mass" :fill="step === 1 ? 'none' : '#161c24'"
                :stroke="step === 1 ? '#7fe9f5' : 'none'" stroke-width="1.4" />

          <!-- shadow the hair throws on the forehead -->
          <path v-if="step >= 2" d="M112 176 Q210 150 308 176 Q210 208 112 176Z" fill="#0b0e13" opacity="0.75" filter="url(#hb-blend)" />

          <!-- 2. direction strokes -->
          <g v-if="step >= 2" clip-path="url(#hb-clip)" :filter="step >= 3 ? 'url(#hb-blend)' : 'none'">
            <path
              v-for="(d, i) in strokes"
              :key="i"
              :d="d"
              fill="none"
              stroke="#05070a"
              :stroke-width="type === 'curly' ? 3 : 2.2"
              stroke-linecap="round"
              :opacity="0.55 + ((i % 4) * 0.11)"
            />
          </g>

          <!-- 3./4. band of light + lifted highlights -->
          <g v-if="step >= 3" clip-path="url(#hb-clip)">
            <ellipse cx="210" cy="150" rx="130" ry="70" fill="url(#hb-band)" />
          </g>
          <g v-if="step >= 4" clip-path="url(#hb-clip)">
            <path
              v-for="(d, i) in highlights"
              :key="'h' + i"
              :d="d"
              fill="none"
              stroke="#e6ebf0"
              :stroke-width="type === 'curly' ? 2.4 : 1.9"
              stroke-linecap="round"
              :opacity="0.16 + ((i % 4) * 0.07)"
              filter="url(#hb-soft)"
            />
          </g>

          <!-- direction arrows (guide, step 2 only) -->
          <g v-if="step === 2" stroke="#ffb45c" stroke-width="1.2" fill="none" opacity="0.9">
            <path v-for="(a, i) in arrows" :key="'a' + i" :d="a" marker-end="url(#hb-arrow)" />
          </g>
          <defs>
            <marker id="hb-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto">
              <path d="M0 0 L10 5 L0 10z" fill="#ffb45c" />
            </marker>
          </defs>

          <text v-if="step >= 3" x="210" y="118" text-anchor="middle"
                font-family="'JetBrains Mono',monospace" font-size="9" letter-spacing="1.6" fill="#ffc98a">
            BAND OF LIGHT
          </text>
        </svg>

        <figcaption class="hairlab__cap">
          <div class="row">
            <button v-for="t in types" :key="t" :class="{ 'is-on': type === t }"
                    :aria-pressed="String(type === t)" @click="type = t">{{ t }}</button>
          </div>
          <div class="row">
            <button v-for="(s, i) in stepNames" :key="s" :class="{ 'is-on': step === i + 1 }"
                    :aria-pressed="String(step === i + 1)" @click="step = i + 1">{{ i + 1 }}. {{ s }}</button>
          </div>
        </figcaption>
      </figure>

      <div class="hairlab__side">
        <transition name="swap" mode="out-in">
          <div :key="step">
            <span class="k mono">Step {{ step }} — {{ stepNames[step - 1] }}</span>
            <h3>{{ copy[step - 1].t }}</h3>
            <p>{{ copy[step - 1].p }}</p>
          </div>
        </transition>

        <div class="tips">
          <article v-for="t in tips" :key="t.t">
            <h4>{{ t.t }}</h4>
            <p>{{ t.p }}</p>
          </article>
        </div>
      </div>
    </div>
  </SectionShell>
</template>

<script setup>
import { ref, computed } from 'vue'
import SectionShell from '../ui/SectionShell.vue'

const types = ['straight', 'wavy', 'curly']
const type = ref('straight')
const step = ref(1)
const stepNames = ['mass', 'direction', 'blend', 'lift']

/* the silhouette of the hair, adjusted per type */
const mass = computed(() => {
  if (type.value === 'curly') {
    return 'M84 262 Q64 96 210 84 Q356 96 336 262 Q348 330 312 352 Q328 220 286 170 Q210 208 134 170 Q92 220 108 352 Q72 330 84 262Z'
  }
  if (type.value === 'wavy') {
    return 'M88 250 Q78 96 210 86 Q342 96 332 250 Q356 344 330 446 L288 438 Q294 348 306 260 Q302 184 274 154 Q210 196 146 154 Q118 184 114 260 Q126 348 132 438 L90 446 Q64 340 88 250Z'
  }
  return 'M92 250 Q84 94 210 86 Q336 94 328 250 Q346 356 338 450 L294 450 Q306 350 304 258 Q300 184 272 156 Q210 198 148 156 Q120 184 116 258 Q114 350 126 450 L82 450 Q74 356 92 250Z'
})

/* stroke families, generated so the direction reads honestly */
const strokes = computed(() => {
  const out = []
  if (type.value === 'curly') {
    for (let i = 0; i < 130; i++) {
      const a = (i * 137.5 * Math.PI) / 180
      const r = 20 + (i % 9) * 13
      const x = 210 + Math.cos(a) * r * 1.35
      const y = 190 + Math.sin(a) * r * 0.95
      const s = 6 + (i % 4) * 2.4
      out.push(`M${x} ${y} a${s} ${s} 0 1 1 ${s * 0.4} ${s * 0.9}`)
    }
    return out
  }
  const wave = type.value === 'wavy'
  for (let i = 0; i < 64; i++) {
    const x0 = 96 + i * 3.7
    const spread = (x0 - 210) / 120
    const x1 = x0 + spread * 34
    if (wave) {
      out.push(`M${x0} ${96 + Math.abs(spread) * 40} C${x0 - 24} ${210}, ${x1 + 30} ${300}, ${x1} ${430}`)
    } else {
      out.push(`M${x0} ${96 + Math.abs(spread) * 40} Q${x0 + spread * 10} ${260} ${x1} ${436}`)
    }
  }
  return out
})

/* Highlights are lifted, not drawn: short, irregular, and only where the
   skull is roundest. Long even strokes read as scratches. */
const highlights = computed(() => {
  const out = []
  if (type.value === 'curly') {
    for (let i = 0; i < 34; i++) {
      const a = (i * 97 * Math.PI) / 180
      const r = 26 + (i % 5) * 15
      const x = 210 + Math.cos(a) * r * 1.25
      const y = 158 + Math.sin(a) * r * 0.6
      out.push(`M${x} ${y} a4.5 4.5 0 1 1 2 3.6`)
    }
    return out
  }
  for (let i = 0; i < 17; i++) {
    const x0 = 126 + i * 10.4
    const spread = (x0 - 210) / 110
    const jitter = ((i * 37) % 11) - 5
    const y0 = 124 + Math.abs(spread) * 34 + jitter
    const len = 34 + ((i * 53) % 22)
    out.push(`M${x0} ${y0} Q${x0 + spread * 5} ${y0 + len * 0.55} ${x0 + spread * 11} ${y0 + len}`)
  }
  // a couple of longer lifts down each tress
  for (let i = 0; i < 5; i++) {
    const x = 96 + i * 7
    out.push(`M${x} ${268 + i * 9} Q${x - 2} 330 ${x - 4} ${392 + i * 6}`)
    out.push(`M${420 - x} ${268 + i * 9} Q${422 - x} 330 ${424 - x} ${392 + i * 6}`)
  }
  return out
})

const arrows = [
  'M210 104 L210 150',
  'M150 118 L128 168',
  'M270 118 L292 168',
  'M118 260 L110 330',
  'M302 260 L310 330'
]

const copy = [
  {
    t: 'Draw the silhouette, nothing else.',
    p: 'One closed shape, exactly like the shadow shapes you have been copying all along. Where does the mass sit against the background? Where does it cut into the forehead? Get that outline right and you have solved most of the hair.'
  },
  {
    t: 'Stroke in the direction it grows.',
    p: 'From the part outward, from the bangs upward, from the nape down. Quick strokes with a flick at the end so they taper. Never scribble and never cross-hatch — crossed strokes cannot be blended into a mass.'
  },
  {
    t: 'Blend along the strokes, and find the band.',
    p: 'Blend in the same direction you drew, never across. Wherever the skull is roundest — usually a broad arc across the crown and forehead — leave a lane of lighter tone. That is the band of light, and it is what tells the eye there is a head under all that hair.'
  },
  {
    t: 'Lift, reapply, repeat.',
    p: 'Flatten the kneaded eraser into a razor edge and pull strands of light out of the dark, reshaping the edge every couple of strokes. Then push some darks back in over them. Hair looks real because of this back-and-forth, not because of any single pass.'
  }
]

const tips = [
  { t: 'Hair casts a shadow', p: 'Where bangs overhang the forehead there is a dark band on the skin. Leaving bright white paper there is one of the most common reasons a portrait reads as pasted together.' },
  { t: 'Curly is circular', p: 'Same procedure, but every stroke and every blend is a tight little circle. Keep it lighter at the outer edge and darker close to the skull, so the mass keeps its volume.' },
  { t: 'Dark hair is not black', p: 'Even the darkest hair carries a #3 or #4 shine along the band of light. Solid black hair reads as a hole in the paper.' },
  { t: 'The part is a giveaway', p: 'A light line where the hair separates, with the darkest values immediately beside it. Get that contrast right and the whole head reads as three-dimensional.' }
]
</script>

<style scoped>
.hairlab {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: clamp(24px, 3.5vw, 56px);
  align-items: start;
}
.hairlab__fig { margin: 0; overflow: hidden; position: sticky; top: 92px; background: #0a0d12; }
.hairlab__svg { display: block; width: 100%; height: auto; }
.hairlab__cap {
  border-top: 1px solid var(--edge-soft);
  padding: 12px 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  justify-content: space-between;
}
.row { display: flex; gap: 4px; flex-wrap: wrap; }
.row button { padding: 7px 12px; font-size: 9px; }

.k { color: var(--signal); font-size: 10px; display: block; margin-bottom: 14px; }
.hairlab__side h3 {
  font-size: clamp(21px, 1.7vw + 13px, 31px);
  font-weight: 400;
  letter-spacing: -0.025em;
  margin-bottom: 16px;
}
.hairlab__side > div > p { color: var(--text-dim); min-height: 130px; }

.tips {
  margin-top: 26px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1px;
  background: var(--edge-soft);
  border: 1px solid var(--edge-soft);
}
.tips article { background: rgba(8, 11, 17, 0.8); padding: 20px 18px; }
.tips h4 {
  font-family: var(--font-mono);
  font-size: 9.5px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--light);
  font-weight: 400;
  margin-bottom: 10px;
}
.tips p { color: var(--text-dim); font-size: 13px; line-height: 1.6; margin: 0; }

.swap-enter-active, .swap-leave-active { transition: opacity 0.28s, transform 0.28s; }
.swap-enter-from { opacity: 0; transform: translateY(10px); }
.swap-leave-to { opacity: 0; transform: translateY(-10px); }

@media (max-width: 980px) {
  .hairlab { grid-template-columns: 1fr; }
  .hairlab__fig { position: static; }
}
</style>
